import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { dispatch } from 'store'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { DEFAULT_MEDIA_DURATION } from 'consts'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { COLORS } from 'consts/colors'
import * as Styled from './styled'
import Tooltip from 'components/Tooltip'
import { creditsSelectors } from 'store/features/credits/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { CrownIcon } from 'containers/SubSidebar/styled'
import AiImageGenerationCredits from './Credits'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { handlePremiumModelClick } from 'utils'
import { THUNDER_ICON } from 'consts/mediaUrls'
import { REF_LINKS } from 'consts/refLinks'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { IAIImageModel } from 'store/types/aiImage'
import { useVisualMediaPlacement } from 'hooks/timeline/useVisualMediaPlacement'
import { useTimelineContext } from 'context/TimelineContext'
import { projectDataSelectors } from 'store/features/projectData/selectors'

const AiImageGenerateFooter = () => {
  const generationCost = useSelector(aiImageSelectors.generationCost)
  const userCredits = useSelector(creditsSelectors.userCredits) || 0
  const targetMediaItemId = useSelector(aiImageSelectors.targetMediaItemId)
  const prompt = useSelector(aiImageSelectors.prompt)
  const userCurrentPlanId = useSelector(userSelectors.userCurrentPlanId)
  const modelCode = useSelector(aiImageSelectors.modelCode)
  const modelsData = useSelector(aiImageSelectors.modelsData) || []
  const selectedModel = modelsData.find(
    (model: IAIImageModel) => model.code === modelCode
  )

  const mediaItems = useSelector(projectDataSelectors.mediaItems)

  const getPlacement = useVisualMediaPlacement()
  const { setPlayHeadCurrentTime, isPlaying } = useTimelineContext()

  const isRegenerating = !!targetMediaItemId

  const targetMediaItem = useMemo(
    () => mediaItems.find(item => item.id === String(targetMediaItemId)),
    [mediaItems, targetMediaItemId]
  )

  const hasInsufficientCredits = userCredits < generationCost
  const openUpgradePopup = useOpenUpgradePopup({ usePlanCheck: true })

  const isRequestingGeneration = useSelector(
    aiImageSelectors.isRequestingGeneration
  )
  const isRefImagesUploading = useSelector(
    aiImageSelectors.isRefImagesUploading
  )
  const isGenerateEnabled = !!prompt?.trim() && !isRefImagesUploading
  const { isDurationAllowed } = useDurationLimitCheck()

  const handleGenerate = useCallback(() => {
    if (handlePremiumModelClick(selectedModel, userCurrentPlanId)) {
      return
    }

    if (hasInsufficientCredits) {
      openUpgradePopup({ ref: REF_LINKS.V_EDITOR_AI_IMAGE_GENERATE })
      return
    }

    if (!isDurationAllowed(DEFAULT_MEDIA_DURATION) || isRequestingGeneration) {
      return
    }

    if (isRegenerating) {
      dispatch(aiImageThunks.regenerateImage())
      if (targetMediaItem) {
        setPlayHeadCurrentTime(targetMediaItem.startTime, isPlaying)
      }
    } else {
      const placement = getPlacement(DEFAULT_MEDIA_DURATION)
      dispatch(aiImageThunks.generateImage(placement))
      setPlayHeadCurrentTime(placement.startTime, isPlaying)
    }
  }, [
    isRequestingGeneration,
    isDurationAllowed,
    hasInsufficientCredits,
    openUpgradePopup,
    isRegenerating,
    selectedModel,
    userCurrentPlanId,
    getPlacement,
    isPlaying,
    setPlayHeadCurrentTime,
    targetMediaItem
  ])

  const title = isRegenerating
    ? translate(T.RE_GENERATE)
    : translate(T.GENERATE_LABEL)

  return (
    <Styled.AiImageGenerateFooter>
      <Tooltip
        tooltipText={translate(T.ENTER_PROMPT_TO_GENERATE)}
        isVisible={!isGenerateEnabled && !isRequestingGeneration}
      >
        <div>
          <Styled.GenerateButton
            onClick={handleGenerate}
            isDisabled={!isGenerateEnabled}
            $isPremium={hasInsufficientCredits}
          >
            <Styled.ButtonContent>
              {hasInsufficientCredits && <CrownIcon />}
              <Styled.SpinningLoaderWrapper
                size={16}
                loaderColor={COLORS.white}
                $isVisible={isRequestingGeneration}
              />
              {title}
              <Styled.CreditsBadge>
                <Styled.ThunderIcon src={THUNDER_ICON} alt="credits" />
                {generationCost}
              </Styled.CreditsBadge>
            </Styled.ButtonContent>
          </Styled.GenerateButton>
        </div>
      </Tooltip>
      <AiImageGenerationCredits />
    </Styled.AiImageGenerateFooter>
  )
}

export default memo(AiImageGenerateFooter)
